import { navigationNamespace } from "@/store/modules/navigation";
import { patientNamespace } from "@/store/modules/patient";
import { Patient } from "@/store/modules/patient/types";
import { towerNamespace } from "@/store/modules/tower";
import { TowerAction } from "@/store/modules/tower/actions";
import {
  TOWER_ALERT_REVIEW_LOAD,
  TOWER_ALERT_REVIEW_UPDATE,
  TOWER_REVIEW_DATA
} from "@/store/modules/tower/mutation_types";
import _ from "lodash";
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { newWindowEventListeners } from "../shared/util";
import AlertAssessmentOrAction from "./tower/AlertAssessmentOrAction";

export const TOWER_IGNORE = "assess.100";

@Component({
  components: { AlertAssessmentOrAction }
})
export default class tower extends Vue {
  @Action(TOWER_ALERT_REVIEW_LOAD, towerNamespace)
  TOWER_ALERT_REVIEW_LOAD!: Function;

  @Action(TOWER_ALERT_REVIEW_UPDATE, towerNamespace)
  TOWER_ALERT_REVIEW_UPDATE!: Function;

  @Getter(TOWER_REVIEW_DATA, towerNamespace)
  towerReviewData!: Array<Record<string, any>>;

  @Getter("current", patientNamespace)
  current!: Patient;

  @Getter("vars", patientNamespace)
  vars!: any;

  @Getter("inWrapper", navigationNamespace)
  inWrapper!: boolean;

  towerNote = "";
  newIssueSelected: string | null = null;
  customIssue = "";
  customIssueRows = 1;

  newIssueObject = {};
  newIssueCommentText = "";

  editing = false;
  disableFormElement = false;

  customIssueFocused = false;

  done = false;

  async doOnce() {
    if (!this.done) {
      this.done = true;
      const ref = this;
      if (!this.inWrapper) {
        newWindowEventListeners(ref, "tower");
      }
      const opSys = this.current.census.opSys;
      const roomSys = this.current.census.roomSys;
      return await this.TOWER_ALERT_REVIEW_LOAD({ opSys, roomSys });
    } else {
      return Promise.resolve();
    }
  }

  @Watch("current")
  async initialLoad() {
    await this.doOnce();
    this.finishSave();
  }

  finishSave() {
    this.resetNote();
    this.resetNewItem();
    this.disableFormElement = false;
    this.customIssueFocused = false;
  }

  async mounted() {
    if (this.current) {
      await this.initialLoad();
    }
  }

  get list(): Array<Record<string, any>> {
    const needsReview = this.current.census.alert
      .filter(alert => {
        return alert.pickID && alert.atom;
      })
      .map(alert => {
        const atomForOrgan = this.vars.atomList[alert.atom];
        const atomForPatient = this.current.body.atoms[alert.atom];

        const listItem: Record<string, any> = Object.assign({}, alert);
        // Flag to increment sequence
        listItem.incrementSequence = true;
        listItem.organ = atomForOrgan.organ;
        listItem.type = "Alert";

        listItem.status = atomForPatient.review!.status;
        listItem.sequence = atomForPatient.review!.sequence;
        listItem.message = atomForPatient.alert;

        listItem.assessment = "";
        listItem.action = "";
        listItem.contact = "";
        listItem.reaction = "";
        listItem.comment = "";
        return listItem;
      });

    const previouslyReviewed: Array<Record<string, any>> = this.towerReviewData
      .filter(alert => {
        return alert.id;
      })
      .map(alert => {
        const atomForOrgan = this.vars.atomList[alert.atom];

        const listItem: Record<string, any> = Object.assign({}, alert);
        listItem.organ = atomForOrgan.organ;
        return listItem;
      });
    const grouped = _.groupBy(previouslyReviewed, "id");

    const filteredNeedsReview = needsReview.filter(item => {
      if (grouped[item.id]) {
        const hasSequence = grouped[item.id].filter(g => {
          return g.sequence === item.sequence;
        });
        if (hasSequence.length && item.status === hasSequence[0].status) {
          return false;
        }
      }
      return true;
    });
    return filteredNeedsReview.concat(previouslyReviewed);
  }

  toggleButtons() {
    this.disableFormElement = !this.disableFormElement;
  }
  get note() {
    const notes = this.towerReviewData.filter(reviewData => {
      return reviewData.type === "Note";
    });
    if (notes.length) {
      return notes[0].comment;
    } else {
      return "";
    }
  }

  resetNote() {
    this.towerNote = this.note;
    this.editing = false;
  }

  resetNewItem() {
    this.newIssueSelected = null;
    this.newIssueCommentText = "";
    this.customIssue = "";
  }

  async saveNote() {
    this.editing = false;
    this.toggleButtons();
    await this.TOWER_ALERT_REVIEW_UPDATE({
      item: { note: this.towerNote },
      type: TowerAction.SAVE_NOTE
    });
    await this.initialLoad();
  }

  async saveComment() {
    const item = {
      atom: "newIssue",
      type: TowerAction.ENTRY,
      id: this.newIssueSelected,
      message: this.customIssue,
      assessment: TOWER_IGNORE,
      comment: this.newIssueCommentText
    };
    this.saveItem(item);
  }

  private async saveItem(item: Record<string, any>) {
    this.disableFormElement = true;
    await this.TOWER_ALERT_REVIEW_UPDATE({ item });
    this.initialLoad();
  }

  createNewIssueObject(id: string) {
    return {
      id,
      atom: "newIssue",
      type: "Entry",
      status: 3,
      assessment: "",
      action: "",
      reaction: "",
      contact: ""
    };
  }

  newIssueSelect(id: string) {
    this.newIssueSelected = id;
    switch (id) {
      case "bigSave":
        if (this.customIssue === "") {
          this.customIssue = "Important intervention occurred";
        }
        break;
      case "newIssue.1":
      case "newIssue.2":
        this.newIssueObject = this.createNewIssueObject(id);
        break;
    }
  }

  get treatmentText() {
    const census = this.current.census;
    if (census.age > 0 && census.age < 18) {
      return "Patient Less Than 18 Years Old";
    } else if (this.treatmentPatient) {
      return "Treatment Patient: Contact Providers";
    } else {
      return "Control Patient: Do Not Contact Providers";
    }
  }

  // If false, not part of the study
  get treatmentPatient(): boolean {
    return this.current.census.treatment === 1;
  }
}
